import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import contactService from '@api/v1/contactService'
import DataGrid from '@components/common/DataGrid'
import ContactForm from '@components/contacts/ContactForm'
import ConfirmModal from '@components/common/ConfirmModal'
import { toast } from '@store/toastStore'
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const FETCH_SIZE = 500

const DetailRow = ({ label, value }) => (
  <div className="flex items-start py-2.5 border-b border-gray-50 last:border-0">
    <dt className="w-32 shrink-0 text-sm text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{value || '--'}</dd>
  </div>
)

const Contacts = () => {
  useDocumentTitle('Contacts')
  const { id: routeContactId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState(null)
  const [editing, setEditing] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState(null)
  const [bulkArchiveRows, setBulkArchiveRows] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])

  const shortcuts = useMemo(() => [
    { key: 'n', meta: true, handler: () => setShowCreate(true) },
  ], [])
  useKeyboardShortcuts(shortcuts)

  const contactsQuery = useQuery({
    queryKey: ['contacts', searchTerm],
    queryFn: () => contactService.getContacts({
      limit: FETCH_SIZE,
      offset: 0,
      searchTerm: searchTerm.length >= 2 ? searchTerm : undefined,
    }),
  })
  const { data, error, refetch } = contactsQuery

  /*
   * CC-53132: three outcomes, not two — see `@api/v1/readState`.
   *
   * This page used to render the grid on `!error` and hand it `data?.contacts ||
   * []`, with a subtitle counting `data?.total || 0`. `isLoading` is
   * `isPending && isFetching`, so it is FALSE for a query that is pending but not
   * in flight — above all a PAUSED one (react-query's default
   * `networkMode: 'online'` while `onlineManager` believes the browser is
   * offline). `queryFn` is never called, nothing throws, nothing is logged, and
   * `data` stays `undefined`. The page therefore settled on "0 contacts" over
   * "No contacts found", with no spinner and no error, for as long as the browser
   * stayed offline — a firm-wide factual claim about the address book, made
   * without ever having asked. Nothing anywhere records that it happened, on
   * either side, because there is no request to find in a log; that is exactly
   * why this class of defect survives server-side investigation.
   *
   * The hook rather than the bare `readState()` because it carries the permanent
   * blocked-read diagnostic (CC-49899 rule: a fail-soft path you cannot observe
   * is indistinguishable from a fix).
   */
  const contactsState = useReadState('contacts-list', contactsQuery)
  const contactsReady = contactsState === 'ready'

  const createMutation = useMutation({
    mutationFn: (contactData) => contactService.createContact(contactData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setShowCreate(false)
      toast.success('Contact created successfully')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create contact')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data: contactData }) =>
      contactService.updateContact(id, contactData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setEditing(false)
      setSelected(null)
      toast.success('Contact updated')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to update contact')
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (contactId) => contactService.archiveContact(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setArchiveTarget(null)
      setSelected(null)
      toast.success('Contact archived')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to archive contact')
    },
  })

  const bulkArchiveMutation = useMutation({
    mutationFn: async (rows) => {
      for (const row of rows) {
        if (row.id) await contactService.archiveContact(row.id)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setBulkArchiveRows(null)
      setSelectedRows([])
      toast.success('Contacts archived')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to archive contacts')
    },
  })

  /*
   * `undefined` has to survive all the way to the grid. `DataGrid` tells an
   * unresolved read from an empty one by `Array.isArray(data)` and swaps its
   * "No contacts found" line for an honest `role="alert"` one (and drops its row
   * count) when the read never landed — so `|| []` here is the defect itself, not
   * a tidy-up. It also covers the case where CB answers with a body that has no
   * `contacts` key at all: something came back, but not a list, and "not known to
   * be empty" is the only truthful reading of that.
   */
  const rows = data?.contacts

  // The route auto-select below needs an array to search AND is a dependency of
  // its own effect, so it gets a memoized coercion rather than a fresh `[]` every
  // render (a ticking value reaching a memo has caused two unrelated-looking bugs
  // in this repo). Keyed on the raw value, so its identity changes only when the
  // read does.
  const contacts = useMemo(() => rows ?? [], [rows])

  const total = data?.total || 0

  // Auto-select contact from route param (e.g. /contacts/:id from search)
  useEffect(() => {
    if (routeContactId && contacts.length > 0 && !selected) {
      const match = contacts.find((c) => c.id === routeContactId)
      if (match) setSelected(match)
    }
  }, [routeContactId, contacts, selected])

  const columns = useMemo(() => [
    {
      id: 'fullName', header: 'Name', accessorKey: 'fullName', size: 200,
      cell: ({ getValue }) => (
        <span className="font-medium text-gray-900">{getValue()}</span>
      ),
    },
    {
      id: 'isClient', header: 'Is Client',
      accessorFn: (row) => row.isClient ? 'Yes' : 'No',
      cell: ({ getValue }) => getValue() === 'Yes'
        ? (
          <span className="text-xs font-medium text-cb-teal-dark
            bg-cb-teal/10 px-2 py-0.5 rounded-full">Yes</span>
        )
        : <span className="text-xs text-gray-500">No</span>,
      size: 90,
    },
    {
      id: 'contactType', header: 'Type',
      accessorKey: 'contactType', size: 110,
    },
    {
      id: 'company', header: 'Company',
      accessorKey: 'company', size: 160,
    },
    { id: 'email', header: 'Email', accessorKey: 'email', size: 200 },
    { id: 'phone', header: 'Phone', accessorKey: 'phone', size: 130 },
    { id: 'status', header: 'Status', accessorKey: 'status', size: 100 },
  ], [])

  const handleRowClick = (row) => {
    if (row.id) {
      navigate(`/contacts/${row.id}`)
    } else {
      setSelected(row)
      setEditing(false)
    }
  }

  const closeDetail = () => {
    setSelected(null)
    setEditing(false)
  }

  const bulkActions = useMemo(() => [
    {
      label: 'Archive Selected',
      variant: 'danger',
      isLoading: bulkArchiveMutation.isPending,
      onClick: (rows) => setBulkArchiveRows(rows),
    },
  ], [bulkArchiveMutation.isPending])

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Contacts</h1>
          {/* CC-53132: the count is a CLAIM about the firm's address book, so it
              renders only behind a COMPLETED read. "0 contacts" beside a read that
              was never sent is not a small number, it is a wrong one — and it is
              the more damaging half of this defect, because a reader scanning the
              header takes the figure at face value without ever looking at the
              grid. Unresolved prints no number at all rather than a placeholder
              zero. `total` keeps its `|| 0` fallback for the resolved case: a
              response that genuinely carries no `total` really is zero as far as
              anyone can tell, and that is a fact about the answer, not the
              absence of one. */}
          {contactsReady && (
            <p className="text-sm text-gray-500 mt-1">
              {total} contact{total !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2.5 bg-cb-accent text-white rounded-lg text-sm
            font-medium hover:bg-cb-accent/90 transition-colors shadow-sm"
        >
          + New Contact
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center
          justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full
            max-h-[90vh] overflow-y-auto p-6"
          >
            <h2 className="text-lg font-display text-gray-900 mb-4">
              New Contact
            </h2>
            {createMutation.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200
                rounded-lg text-sm text-red-600"
              >
                {createMutation.error.message || 'Failed to create contact'}
              </div>
            )}
            <ContactForm
              onSave={(formData) => createMutation.mutate(formData)}
              onCancel={() => setShowCreate(false)}
              saving={createMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* Detail/Edit Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center
          justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full
            max-h-[90vh] overflow-y-auto p-6"
          >
            {editing ? (
              <>
                <h2 className="text-lg font-display text-gray-900 mb-4">
                  Edit Contact
                </h2>
                {updateMutation.error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200
                    rounded-lg text-sm text-red-600"
                  >
                    {updateMutation.error.message || 'Failed to update'}
                  </div>
                )}
                <ContactForm
                  contact={selected}
                  onSave={(formData) => updateMutation.mutate({
                    id: selected.id, data: formData,
                  })}
                  onCancel={() => setEditing(false)}
                  saving={updateMutation.isPending}
                />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-display text-gray-900">
                    {selected.fullName}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setArchiveTarget(selected)}
                      className="px-3 py-1.5 text-sm font-medium text-red-600
                        bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      Archive
                    </button>
                    <button
                      onClick={() => setEditing(true)}
                      className="px-3 py-1.5 text-sm font-medium text-white
                        bg-cb-accent rounded-lg hover:bg-cb-accent/90
                        transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={closeDetail}
                      className="px-3 py-1.5 text-sm font-medium text-gray-500
                        hover:text-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <dl>
                  <DetailRow label="Name" value={selected.fullName} />
                  <DetailRow label="Company" value={selected.company} />
                  <DetailRow label="Type" value={selected.contactType} />
                  <DetailRow label="Email" value={selected.email} />
                  <DetailRow label="Phone" value={selected.phone} />
                  <DetailRow label="Status" value={selected.status} />
                  <DetailRow
                    label="Is Client"
                    value={selected.isClient ? 'Yes' : 'No'}
                  />
                </dl>
              </>
            )}
          </div>
        </div>
      )}

      {/* Archive Confirm */}
      {archiveTarget && (
        <ConfirmModal
          title="Archive Contact"
          message={`Are you sure you want to archive "${archiveTarget.fullName}"? Their status will be set to Inactive.`}
          confirmLabel="Archive"
          variant="danger"
          isLoading={archiveMutation.isPending}
          onConfirm={() => archiveMutation.mutate(archiveTarget.id)}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {/* Bulk Archive Confirm */}
      {bulkArchiveRows && (
        <ConfirmModal
          title="Archive Contacts"
          message={`Are you sure you want to archive ${bulkArchiveRows.length} contact(s)?`}
          confirmLabel="Archive All"
          variant="danger"
          isLoading={bulkArchiveMutation.isPending}
          onConfirm={() => bulkArchiveMutation.mutate(bulkArchiveRows)}
          onCancel={() => setBulkArchiveRows(null)}
        />
      )}

      {/* Search */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 max-w-md relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4
              text-gray-500"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round" strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5
                7.5 0 0010.607 10.607z"
            />
          </svg>
          {/*
            aria-label, not placeholder-only: a placeholder contributes NOTHING
            to the accessible name, so a placeholder-only input is a control with
            no name at all -- and it vanishes on the first keystroke besides
            (CC-53430).
          */}
          <input
            type="text"
            placeholder="Search contacts..."
            aria-label="Search contacts"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border
              border-gray-200 rounded-lg text-sm text-gray-900
              placeholder:text-gray-500 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
      </div>

      {/* Error state. The panel already existed and already stood in front of the
          grid, which is right — but it had no `role="alert"` and no way out, so
          the only recovery was a full page reload. `refetch` comes from
          react-query, so a retry is free. The second sentence is not decoration:
          the user has just been looking at a Contacts page with no contacts on
          it, and needs to be told that is not a statement about their data. */}
      {contactsState === 'failed' ? (
        <div
          role="alert"
          className="bg-white rounded-xl shadow-sm border border-gray-100
            p-12 text-center"
        >
          <p className="text-sm text-red-500">
            Failed to load contacts: {error.message}
          </p>
          <p className="mt-1.5 text-sm text-gray-500">
            This is not an empty address book — nothing was read, so no contact is
            missing and none has been archived.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-rc-control bg-white px-3
              py-1.5 text-xs font-medium text-cb-accent-text hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      ) : contactsState === 'blocked' ? (
        /* The state that used to render as "0 contacts" over "No contacts found".
           react-query PAUSED the fetch, so `queryFn` was never called: no request,
           no error, no spinner, nothing in any log on either side. A spinner
           forever would be nearly as bad as the empty state, so say what actually
           happened and offer the same retry the failure branch does. */
        <div
          role="alert"
          className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm
            text-amber-900"
        >
          <p className="font-medium">
            Can&rsquo;t reach the server, so we haven&rsquo;t loaded your contacts.
          </p>
          <p className="mt-1 text-amber-800">
            This is not an empty address book — the contact list was never read.
            Your connection looks offline; check it and try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3
              py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      ) : (
        <DataGrid
          columns={columns}
          /* Deliberately the raw value, not the memoized coercion above: the grid
             is the component that can tell `undefined` from `[]`, and stripping
             that here is what made a never-sent read look like an empty firm. */
          data={rows}
          /* 'idle' is folded into loading. This query carries no `enabled:`
             option, so it cannot reach that state today — but every branch here
             is gated on a NAMED state, and an unhandled one would hand the grid
             `isLoading={false}` with `data === undefined`, i.e. the honest
             unresolved line with no explanation of why. Folding it in costs
             nothing and cannot strand the page on a permanent spinner while that
             stays true; if anyone adds an `enabled:` gate, revisit this line
             FIRST, because then idle becomes the ordinary "not asked yet" and
             wants its own copy, not a spinner. */
          isLoading={contactsState === 'loading' || contactsState === 'idle'}
          emptyMessage="No contacts found"
          unresolvedMessage="We couldn't load your contacts, so this is not known to be an empty list. Reload to try again."
          loadingMessage="Loading contacts..."
          onRowClick={handleRowClick}
          storageKey="contacts"
          enableSelection={true}
          onSelectionChange={setSelectedRows}
          bulkActions={bulkActions}
          exportFilename="contacts"
        />
      )}
    </div>
  )
}

export default Contacts
